import { useMergedRef } from '@mantine/hooks';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { forwardRef, useEffect, useRef } from 'react';
import { controlStyles } from '../styles/controls';
import { colors } from '../theme/colors.stylex';
import { controlMetrics, controlTypography } from '../theme/controls.stylex';
import type { TextAreaProps } from './TextArea.types';

const styles = stylex.create({
  disabled: { pointerEvents: 'none' },
  field: {
    'boxSizing': 'border-box',
    'width': '100%',
    'borderRadius': controlMetrics.radius,
    'paddingLeft': controlMetrics.paddingInline,
    'paddingRight': controlMetrics.paddingInline,
    'paddingTop': controlMetrics.paddingBlock,
    'paddingBottom': controlMetrics.paddingBlock,
    'fontFamily': 'inherit',
    'fontSize': controlTypography.fontSize,
    'fontWeight': 'inherit',
    'lineHeight': controlTypography.lineHeight,
    'outlineStyle': 'none',
    'resize': 'vertical',
    '::placeholder': { color: colors.placeholder, opacity: 1 },
  },
});

/**
 * TextArea.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      readonly = false,
      disabled = false,
      onChange,
      onKeyUp,
      onFocus,
      onBlur,
      onKeyDown,
      onSelectionChange,
      id,
      name,
      rows = 3,
      cols,
      value,
      placeholder,
      showError,
      extraClassNames = '',
      xstyle,
      testId,
      autoFocus = false,
    },
    ref,
  ) => {
    const styleProps = stylex.props(
      controlStyles.base,
      styles.field,
      showError && controlStyles.invalid,
      disabled && controlStyles.disabled,
      disabled && styles.disabled,
      xstyle,
    );
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mergedRef = useMergedRef(ref, textareaRef);

    // Handle selection change events
    useEffect(() => {
      if (!onSelectionChange || !textareaRef.current) {
        return;
      }

      const textarea = textareaRef.current;

      const handleSelectionChange = (e: Event) => {
        onSelectionChange(e);
      };

      // Add event listeners for selection changes
      textarea.addEventListener('selectionchange', handleSelectionChange);

      // Also listen for mouse and keyboard events that change selection
      textarea.addEventListener('mouseup', handleSelectionChange);
      textarea.addEventListener('keyup', handleSelectionChange);

      return () => {
        textarea.removeEventListener('selectionchange', handleSelectionChange);
        textarea.removeEventListener('mouseup', handleSelectionChange);
        textarea.removeEventListener('keyup', handleSelectionChange);
      };
    }, [onSelectionChange]);

    return (
      <textarea
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        value={value}
        {...styleProps}
        className={clsx(styleProps.className, extraClassNames)}
        aria-invalid={showError || undefined}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        rows={rows}
        cols={cols}
        autoFocus={autoFocus}
      />
    );
  },
);
TextArea.displayName = 'TextArea';
